import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import axios, { API } from "@/lib/apiClient";
import { Crown, Users2, ScanSearch, DollarSign, Flame, Send, Inbox, Mail, CheckCircle2, Zap, RadioTower, Rocket, Copy, ExternalLink, Loader2, ShieldQuestion, BadgeCheck, XCircle, Sparkles, Download, ServerCog, Power, AlertTriangle, History } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { OutreachModal } from "../components/OutreachModal";
import { DirectorsOverridePanel } from "../components/DirectorsOverridePanel";
import { M2mKeysPanel } from "../components/M2mKeysPanel";
import { SovereignAlertsPanel } from "../components/SovereignAlertsPanel";
import { ProviderRankingsPanel } from "../components/ProviderRankingsPanel";
import { AdminRevenuePanel } from "../components/AdminRevenuePanel";
import { toast } from "sonner";

const towerSlug = (brandName) =>
  (brandName || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");


export const AdminDirectorate = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [outreachBrand, setOutreachBrand] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [crawls, setCrawls] = useState(null);
  const [submittingBrand, setSubmittingBrand] = useState(null);
  const [briefings, setBriefings] = useState([]);
  const [briefingActionId, setBriefingActionId] = useState(null);
  const [killSwitchBusy, setKillSwitchBusy] = useState(false);
  const [killSwitchLog, setKillSwitchLog] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [m2mKeys, setM2mKeys] = useState([]);
  const [sovereignAlerts, setSovereignAlerts] = useState([]);
  const [providerRankings, setProviderRankings] = useState([]);

  const fetchKillSwitchLog = () => axios.get(`${API}/admin/kill-switch/log`).then((res) => setKillSwitchLog(res.data)).catch(() => {});
  const fetchOverrides = () => axios.get(`${API}/admin/overrides`).then((res) => setOverrides(res.data)).catch(() => {});
  const fetchM2mKeys = () => axios.get(`${API}/admin/m2m-keys`).then((res) => setM2mKeys(res.data)).catch(() => {});
  const fetchSovereignAlerts = () => axios.get(`${API}/admin/sovereign-alerts`).then((res) => setSovereignAlerts(res.data)).catch(() => {});
  const fetchProviderRankings = () => axios.get(`${API}/admin/sovereign-pulse/provider-rankings`).then((res) => setProviderRankings(res.data)).catch(() => {});

  useEffect(() => {
    axios
      .get(`${API}/admin/directorate`)
      .then((res) => setData(res.data))
      .catch(() => setForbidden(true));
    axios
      .get(`${API}/admin/support-tickets`)
      .then((res) => setTickets(res.data))
      .catch(() => {});
    axios
      .get(`${API}/admin/truth-crawls`)
      .then((res) => setCrawls(res.data))
      .catch(() => {});
    axios
      .get(`${API}/admin/threat-briefings`)
      .then((res) => setBriefings(res.data))
      .catch(() => {});
    fetchKillSwitchLog();
    fetchOverrides();
    fetchM2mKeys();
    fetchSovereignAlerts();
    fetchProviderRankings();
  }, []);

  const resolveTicket = async (ticketId) => {
    try {
      await axios.put(`${API}/admin/support-tickets/${ticketId}/status`, { status: "resolved" });
      setTickets((prev) => prev.map((t) => (t.id === ticketId ? { ...t, status: "resolved" } : t)));
    } catch {
      toast.error("Could not update ticket status.");
    }
  };

  const towerUrlFor = (brandName) => `${process.env.REACT_APP_BACKEND_URL}/truth/${towerSlug(brandName)}`;

  const copyTowerUrl = async (brandName) => {
    const url = towerUrlFor(brandName);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Tower URL copied to clipboard.");
    } catch {
      toast.error("Could not copy. Copy manually: " + url);
    }
  };

  const openSubmissionTool = async (brandName, label, toolUrl) => {
    await copyTowerUrl(brandName);
    window.open(toolUrl, "_blank", "noopener,noreferrer");
    toast.info(`Tower URL copied - paste it into ${label}.`);
  };

  const submitIndexNow = async (brandName) => {
    const slug = towerSlug(brandName);
    setSubmittingBrand(brandName);
    try {
      const res = await axios.post(`${API}/admin/tower/submit-indexnow`, { brand_slug: slug });
      if (res.data.success) {
        toast.success(`Submitted to IndexNow (Bing/Yandex/Seznam) for ${brandName}.`);
      } else {
        toast.error("IndexNow submission failed. Try again shortly.");
      }
    } catch {
      toast.error("IndexNow submission failed. Try again shortly.");
    } finally {
      setSubmittingBrand(null);
    }
  };

  const approveBriefing = async (id) => {
    setBriefingActionId(id);
    try {
      await axios.post(`${API}/admin/threat-briefings/${id}/approve`);
      toast.success("Briefing sealed and delivered - Director's Verified.");
      setBriefings((prev) => prev.map((b) => (b.id === id ? { ...b, status: "delivered", digital_notary: { verified_by: "founder" } } : b)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not approve briefing.");
    } finally {
      setBriefingActionId(null);
    }
  };

  const toggleKillSwitch = async () => {
    const next = !data.kill_switch?.signals_paused;
    setKillSwitchBusy(true);
    try {
      await axios.put(`${API}/admin/kill-switch`, { signals_paused: next });
      setData((prev) => ({ ...prev, kill_switch: { ...prev.kill_switch, signals_paused: next } }));
      toast.success(next ? "Director's Kill-Switch engaged - outgoing Signal emails are paused." : "Director's Kill-Switch disengaged - Signals are flowing again.");
      fetchKillSwitchLog();
    } catch {
      toast.error("Could not update the Kill-Switch.");
    } finally {
      setKillSwitchBusy(false);
    }
  };

  const runAiValidation = async (id) => {
    setBriefingActionId(id);
    try {
      const res = await axios.post(`${API}/admin/threat-briefings/${id}/validate`);
      setBriefings((prev) => prev.map((b) => (b.id === id ? res.data : b)));
      toast.success(res.data.status === "delivered" ? "AI Proxy validated as functional - delivered." : "AI Proxy flagged this as a likely hallucination - rejected.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Final Validation failed.");
    } finally {
      setBriefingActionId(null);
    }
  };

  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const rejectBriefing = async (id) => {
    if (!rejectReason.trim()) return;
    setBriefingActionId(id);
    try {
      await axios.post(`${API}/admin/threat-briefings/${id}/reject`, { reason: rejectReason.trim() });
      setBriefings((prev) => prev.map((b) => (b.id === id ? { ...b, status: "rejected", rejection_reason: rejectReason.trim() } : b)));
      toast.success("Briefing rejected.");
      setRejectingId(null);
      setRejectReason("");
    } catch {
      toast.error("Could not reject briefing.");
    } finally {
      setBriefingActionId(null);
    }
  };

  if (forbidden) {
    return <p className="p-10 text-center text-sovereign" data-testid="directorate-forbidden">Root Admin access only.</p>;
  }
  if (!data) {
    return <div className="flex min-h-[60vh] items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-sovereign border-t-transparent" /></div>;
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-12" data-testid="admin-directorate-page">
      <div className="mb-8 flex items-center gap-2">
        <Crown className="h-6 w-6 text-sovereign" />
        <h1 className="font-heading text-3xl font-bold text-white">Sovereign Directorate</h1>
      </div>

      <div className="mb-10 grid gap-4 sm:grid-cols-4">
        <div className="rounded-lg border border-surface-border bg-surface-card p-6" data-testid="directorate-total-users">
          <Users2 className="mb-2 h-5 w-5 text-openai-cyan" />
          <p className="font-heading text-3xl font-bold text-white">{data.users.length}</p>
          <p className="text-sm text-slate-500">Registered Users</p>
        </div>
        <div className="rounded-lg border border-surface-border bg-surface-card p-6" data-testid="directorate-total-audits">
          <ScanSearch className="mb-2 h-5 w-5 text-claude-purple" />
          <p className="font-heading text-3xl font-bold text-white">{data.total_audits}</p>
          <p className="text-sm text-slate-500">Audits Conducted</p>
        </div>
        <div className="rounded-lg border border-sovereign/40 bg-sovereign/5 p-6" data-testid="directorate-revenue-pulse">
          <DollarSign className="mb-2 h-5 w-5 text-sovereign" />
          <p className="font-heading text-3xl font-bold text-white">${data.revenue_pulse.total_amount.toFixed(0)}</p>
          <p className="text-sm text-slate-500">Revenue Pulse - {data.revenue_pulse.total_successful_payments} checkouts</p>
        </div>
        <div className="rounded-lg border border-surface-border bg-surface-card p-6" data-testid="directorate-payments-healthy">
          <Zap className="mb-2 h-5 w-5 text-warning-amber" />
          <div className="flex items-center gap-2">
            <span
              className={
                data.payments_healthy
                  ? "h-3 w-3 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]"
                  : "h-3 w-3 rounded-full bg-sovereign shadow-[0_0_8px_rgba(255,42,59,0.8)]"
              }
              data-testid="payments-healthy-light"
            />
            <p className="font-heading text-3xl font-bold text-white">{data.payments_healthy ? "Fueled" : "Cold"}</p>
          </div>
          <p className="text-sm text-slate-500 whitespace-nowrap">Payments Healthy</p>
        </div>
      </div>

      <div className="mb-10" data-testid="infrastructure-core-panel">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ServerCog className="h-4 w-4 text-openai-cyan" />
            <h2 className="font-heading text-xl font-bold text-white">Infrastructure Core &mdash; Machine Integrity Pulse</h2>
          </div>
          <div
            role="button"
            tabIndex={0}
            aria-disabled={killSwitchBusy}
            onClick={() => !killSwitchBusy && toggleKillSwitch()}
            className={
              data.kill_switch?.signals_paused
                ? "flex cursor-pointer items-center gap-2 rounded-md bg-sovereign px-3 py-1.5 text-sm font-medium text-white hover:bg-sovereign/90"
                : "flex cursor-pointer items-center gap-2 rounded-md border border-surface-border bg-transparent px-3 py-1.5 text-sm font-medium text-slate-200 hover:text-white"
            }
            data-testid="kill-switch-toggle-btn"
          >
            <Power className="h-3.5 w-3.5" />
            {data.kill_switch?.signals_paused ? "Kill-Switch Engaged - Resume Signals" : "Director's Kill-Switch"}
            <Switch checked={!!data.kill_switch?.signals_paused} className="pointer-events-none ml-1" data-testid="kill-switch-indicator" />
          </div>
        </div>
        <p className="mb-4 text-xs text-slate-500">
          Independent Node Watcher &mdash; runs on its own loop, separate from brand audits. Note this is distinct from Brand Reputation/Visibility signals above.
        </p>
        {data.kill_switch?.signals_paused && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-sovereign/50 bg-sovereign/10 p-3 text-sm text-sovereign" data-testid="kill-switch-active-banner">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" /> Kill-Switch engaged: no outgoing Signal emails/SMS will send until you resume.
          </div>
        )}
        {killSwitchLog.length > 0 && (
          <details className="mb-4 rounded-md border border-surface-border bg-obsidian p-3" data-testid="kill-switch-log">
            <summary className="flex cursor-pointer items-center gap-2 text-xs font-semibold text-slate-400">
              <History className="h-3.5 w-3.5" /> Kill-Switch Audit Log ({killSwitchLog.length})
            </summary>
            <ul className="mt-2 space-y-1">
              {killSwitchLog.map((l) => (
                <li key={l.id} className="text-xs text-slate-500" data-testid={`kill-switch-log-row-${l.id}`}>
                  {l.admin_email} {l.signals_paused ? "engaged" : "disengaged"} the Kill-Switch &mdash; {new Date(l.created_at).toLocaleString()}
                </li>
              ))}
            </ul>
          </details>
        )}
        <div className="mb-4 grid gap-3 sm:grid-cols-4">
          {data.dual_core_status.system_nodes.map((n) => (
            <div key={n.id} className="rounded-lg border border-surface-border bg-surface-card p-4" data-testid={`system-node-${n.id}`}>
              <p className="text-sm font-semibold text-white">{n.label}</p>
              <p className="mt-1 text-xs uppercase tracking-wide text-slate-500">{n.status}</p>
              <p className="text-xs text-slate-500">{n.integrity_score !== null ? `${n.integrity_score}/100` : "Pending first pulse"}</p>
            </div>
          ))}
        </div>
        <p className="mb-2 text-xs text-slate-500">
          {data.dual_core_status.healthy_nodes_watched}/{data.dual_core_status.active_nodes_watched} client-added nodes healthy.
        </p>
        {data.dual_core_status.at_risk_nodes.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="infrastructure-core-empty">No infrastructure risk detected on any watched node.</p>
        ) : (
          <div className="space-y-2" data-testid="at-risk-nodes-list">
            {data.dual_core_status.at_risk_nodes.map((n) => (
              <div key={n.id} className="flex items-center justify-between rounded-md border border-warning-amber/40 bg-warning-amber/5 p-3" data-testid={`at-risk-node-${n.id}`}>
                <div>
                  <p className="text-sm font-semibold text-white">{n.brand_name} &mdash; {n.label}</p>
                  <p className="text-xs text-slate-500">{n.url}</p>
                </div>
                <span className="text-xs font-bold uppercase tracking-wide text-warning-amber">{n.display_label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mb-10 grid gap-6 lg:grid-cols-1">
        <AdminRevenuePanel data={data.subscription_dashboard} />
        <SovereignAlertsPanel alerts={sovereignAlerts} />
        <ProviderRankingsPanel rankings={providerRankings} />
        <DirectorsOverridePanel overrides={overrides} onOverrideCreated={fetchOverrides} />
        <M2mKeysPanel keys={m2mKeys} onKeysChanged={fetchM2mKeys} />
      </div>

      <div className="mb-10">
        <div className="mb-3 flex items-center gap-2">
          <Flame className="h-4 w-4 text-warning-amber" />
          <h2 className="font-heading text-xl font-bold text-white">High-Intent Brands</h2>
        </div>
        <div className="overflow-x-auto rounded-lg border border-surface-border" data-testid="directorate-high-intent-table">
          <table className="w-full text-sm">
            <thead className="bg-surface-card text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Brand</th>
                <th className="px-4 py-3">Audits</th>
                <th className="px-4 py-3">Sovereign Red Conflicts</th>
                <th className="px-4 py-3">Outreach</th>
                <th className="px-4 py-3">Tower Push</th>
              </tr>
            </thead>
            <tbody>
              {data.high_intent_brands.map((b) => (
                <tr key={b.brand_name} className="border-t border-surface-border" data-testid={`high-intent-row-${b.brand_name}`}>
                  <td className="px-4 py-3 text-white">
                    <span className="flex items-center gap-2">
                      {b.brand_name}
                      {b.sent && (
                        <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-400" data-testid={`sent-badge-${b.brand_name}`}>
                          Sent
                        </span>
                      )}
                    </span>
                    {b.contact_email && <span className="mt-0.5 block text-xs text-slate-500">{b.contact_email}</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-400">{b.audit_count}</td>
                  <td className="px-4 py-3 font-semibold text-sovereign">{b.hallucination_total}</td>
                  <td className="px-4 py-3">
                    <Button
                      size="sm"
                      disabled={!b.has_completed_audit}
                      title={!b.has_completed_audit ? "No completed audit yet for this brand" : undefined}
                      onClick={() => setOutreachBrand(b.brand_name)}
                      className="gap-2 bg-sovereign text-white hover:bg-sovereign/90 disabled:cursor-not-allowed disabled:opacity-40"
                      data-testid={`generate-outreach-btn-${b.brand_name}`}
                    >
                      <Send className="h-3.5 w-3.5" /> Generate Outreach
                    </Button>
                  </td>
                  <td className="px-4 py-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!b.has_completed_audit || submittingBrand === b.brand_name}
                          title={!b.has_completed_audit ? "No completed audit yet for this brand" : undefined}
                          className="gap-2 border-surface-border text-slate-200 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                          data-testid={`tower-push-btn-${b.brand_name}`}
                        >
                          {submittingBrand === b.brand_name ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Rocket className="h-3.5 w-3.5" />
                          )}
                          Submit to Directories
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-64" data-testid={`tower-push-menu-${b.brand_name}`}>
                        <DropdownMenuLabel>Push Tower URL</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => copyTowerUrl(b.brand_name)} data-testid={`tower-copy-${b.brand_name}`}>
                          <Copy className="mr-2 h-3.5 w-3.5" /> Copy Tower URL
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => submitIndexNow(b.brand_name)} data-testid={`tower-indexnow-${b.brand_name}`}>
                          <Rocket className="mr-2 h-3.5 w-3.5" /> Submit via IndexNow (Bing/Yandex)
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openSubmissionTool(b.brand_name, "Google Search Console", "https://search.google.com/search-console")}
                          data-testid={`tower-google-${b.brand_name}`}
                        >
                          <ExternalLink className="mr-2 h-3.5 w-3.5" /> Open Google Search Console
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openSubmissionTool(b.brand_name, "Bing Webmaster Tools", "https://www.bing.com/webmasters/url-submission")}
                          data-testid={`tower-bing-${b.brand_name}`}
                        >
                          <ExternalLink className="mr-2 h-3.5 w-3.5" /> Open Bing Webmaster Tools
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openSubmissionTool(b.brand_name, "Yandex Webmaster", "https://webmaster.yandex.com/")}
                          data-testid={`tower-yandex-${b.brand_name}`}
                        >
                          <ExternalLink className="mr-2 h-3.5 w-3.5" /> Open Yandex Webmaster
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openSubmissionTool(b.brand_name, "Crunchbase", "https://www.crunchbase.com/add-new")}
                          data-testid={`tower-crunchbase-${b.brand_name}`}
                        >
                          <ExternalLink className="mr-2 h-3.5 w-3.5" /> Add to Crunchbase (Business Directory)
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {outreachBrand && (
        <OutreachModal
          brandName={outreachBrand}
          onClose={() => setOutreachBrand(null)}
          onSent={(brandName) =>
            setData((prev) => ({
              ...prev,
              high_intent_brands: prev.high_intent_brands.map((b) =>
                b.brand_name === brandName ? { ...b, sent: true } : b
              ),
            }))
          }
        />
      )}

      <div>
        <h2 className="mb-3 font-heading text-xl font-bold text-white">Recent Audits (Attribution)</h2>
        <div className="overflow-x-auto rounded-lg border border-surface-border" data-testid="directorate-recent-audits-table">
          <table className="w-full text-sm">
            <thead className="bg-surface-card text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Brand</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Tier</th>
                <th className="px-4 py-3">Initiated By</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_audits.map((a) => (
                <tr key={a.id} className="border-t border-surface-border" data-testid={`recent-audit-row-${a.id}`}>
                  <td className="px-4 py-3 text-white">{a.brand_name}</td>
                  <td className="px-4 py-3 text-slate-400">{a.status}</td>
                  <td className="px-4 py-3 text-slate-400">{a.paid_tier}</td>
                  <td className="px-4 py-3 text-slate-400">{a.auto_scan ? "Auto Re-Scan" : a.initiated_by || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-10">
        <div className="mb-3 flex items-center gap-2">
          <Inbox className="h-4 w-4 text-openai-cyan" />
          <h2 className="font-heading text-xl font-bold text-white">Support Inbox - Custom Intelligence Requests</h2>
        </div>
        {tickets.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="support-inbox-empty">No Custom Intelligence Requests on file.</p>
        ) : (
          <div className="space-y-3" data-testid="support-inbox-list">
            {tickets.map((t) => (
              <div key={t.id} className="rounded-lg border border-surface-border bg-surface-card p-5" data-testid={`support-ticket-row-${t.id}`}>
                <div className="mb-2 flex items-center justify-between">
                  <div>
                    <p className="font-heading text-sm font-semibold text-white">{t.subject}</p>
                    <p className="text-xs text-slate-500">{t.name || "Unknown"} &middot; {t.email}</p>
                  </div>
                  <span
                    className={
                      t.status === "resolved"
                        ? "rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-400"
                        : "rounded-full bg-warning-amber/15 px-2 py-0.5 text-xs font-semibold text-warning-amber"
                    }
                    data-testid={`support-ticket-status-${t.id}`}
                  >
                    {t.status === "resolved" ? "Resolved" : "Open"}
                  </span>
                </div>
                <p className="mb-3 whitespace-pre-wrap text-sm text-slate-400">{t.message}</p>
                <div className="flex gap-2">
                  <a href={`mailto:${t.email}?subject=${encodeURIComponent("Re: " + t.subject)}`} data-testid={`support-ticket-reply-${t.id}`}>
                    <Button size="sm" variant="outline" className="gap-2 border-surface-border text-slate-200 hover:text-white">
                      <Mail className="h-3.5 w-3.5" /> Reply via Email
                    </Button>
                  </a>
                  {t.status !== "resolved" && (
                    <Button
                      size="sm"
                      onClick={() => resolveTicket(t.id)}
                      className="gap-2 bg-truth-green text-obsidian hover:bg-truth-green/90"
                      data-testid={`support-ticket-resolve-${t.id}`}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" /> Mark Resolved
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-10">
        <div className="mb-3 flex items-center gap-2">
          <RadioTower className="h-4 w-4 text-truth-green" />
          <h2 className="font-heading text-xl font-bold text-white">Broadcast Tower - Crawl Activity</h2>
        </div>
        {!crawls || crawls.rows.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="crawl-activity-empty">No AI/search bot crawls logged yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-surface-border" data-testid="crawl-activity-table">
            <table className="w-full text-sm">
              <thead className="bg-surface-card text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Brand Slug</th>
                  <th className="px-4 py-3">Bot</th>
                  <th className="px-4 py-3">Hits</th>
                  <th className="px-4 py-3">Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {crawls.rows.map((r, i) => (
                  <tr key={i} className="border-t border-surface-border" data-testid={`crawl-row-${i}`}>
                    <td className="px-4 py-3 text-white">{r.brand_slug}</td>
                    <td className="px-4 py-3 text-truth-green">{r.bot}</td>
                    <td className="px-4 py-3 text-slate-400">{r.count}</td>
                    <td className="px-4 py-3 text-slate-500">{r.last_seen ? new Date(r.last_seen).toLocaleString() : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="border-t border-surface-border bg-surface-card px-4 py-2 text-xs text-slate-500" data-testid="crawl-total-bot-hits">
              Total bot hits: {crawls.total_bot_hits}
            </p>
          </div>
        )}
      </div>

      <div className="mt-10" data-testid="command-shield-panel">
        <div className="mb-3 flex items-center gap-2">
          <ShieldQuestion className="h-4 w-4 text-warning-amber" />
          <h2 className="font-heading text-xl font-bold text-white">Command Shield &mdash; Founder's Review Queue</h2>
        </div>
        <p className="mb-4 text-xs text-slate-500">
          Every Sovereign Defense Threat Briefing across all VIP clients. Hidden from clients until sealed &mdash; approve yourself, or let the AI Proxy (Buddy) cross-reference it against real-time web search first.
        </p>
        {briefings.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="command-shield-empty">No Threat Briefings in the queue.</p>
        ) : (
          <div className="space-y-3" data-testid="command-shield-list">
            {briefings.map((b) => (
              <div key={b.id} className="rounded-lg border border-surface-border bg-surface-card p-5" data-testid={`briefing-row-${b.id}`}>
                <div className="mb-2 flex items-center justify-between">
                  <p className="font-heading text-sm font-semibold text-white">{b.brand_name}</p>
                  <span
                    className={
                      b.status === "delivered"
                        ? "rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-400"
                        : b.status === "rejected"
                        ? "rounded-full bg-sovereign/15 px-2 py-0.5 text-xs font-semibold text-sovereign"
                        : "rounded-full bg-warning-amber/15 px-2 py-0.5 text-xs font-semibold text-warning-amber"
                    }
                    data-testid={`briefing-status-${b.id}`}
                  >
                    {b.status === "delivered" ? "Delivered" : b.status === "rejected" ? "Rejected" : "Pending Review"}
                  </span>
                </div>
                {b.unreported_risk && <p className="mb-1 text-xs text-slate-400"><span className="text-warning-amber">Unreported Risk:</span> {b.unreported_risk.description}</p>}
                {b.counter_signal && <p className="mb-1 text-xs text-slate-400"><span className="text-warning-amber">Counter-Signal:</span> {b.counter_signal.directive}</p>}
                {(b.hijack_alerts || []).map((h, i) => (
                  <p key={i} className="mb-1 text-xs text-slate-400"><span className="text-sovereign">Hijack:</span> {h.competitor_name} captured "{h.hijacked_keyword}"</p>
                ))}
                {b.ai_validation && <p className="mt-2 text-xs italic text-slate-500">AI Proxy verdict: {b.ai_validation.verdict} &mdash; {b.ai_validation.rationale}</p>}
                {b.rejection_reason && <p className="mt-2 text-xs italic text-slate-500">Rejection reason: {b.rejection_reason}</p>}
                {b.digital_notary && (
                  <p className="mt-2 text-xs font-bold uppercase text-[#D4AF37]" data-testid={`briefing-notary-${b.id}`}>
                    Director's Verified &mdash; sealed by {b.digital_notary.verified_by === "founder" ? "The Founder" : "AI Proxy"}
                    {" "}
                    <a
                      href={`${API}/admin/threat-briefings/${b.id}/certificate`}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-2 inline-flex items-center gap-1 underline hover:text-[#D4AF37]/80"
                      data-testid={`briefing-certificate-${b.id}`}
                    >
                      <Download className="h-3 w-3" /> Certificate
                    </a>
                  </p>
                )}
                {b.status === "pending_review" && (
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      disabled={briefingActionId === b.id}
                      onClick={() => approveBriefing(b.id)}
                      className="gap-2 bg-[#D4AF37] text-obsidian hover:bg-[#D4AF37]/90"
                      data-testid={`briefing-approve-${b.id}`}
                    >
                      <BadgeCheck className="h-3.5 w-3.5" /> Approve &amp; Seal
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={briefingActionId === b.id}
                      onClick={() => runAiValidation(b.id)}
                      className="gap-2 border-surface-border text-slate-200 hover:text-white"
                      data-testid={`briefing-validate-${b.id}`}
                    >
                      <Sparkles className="h-3.5 w-3.5" /> Run AI Validation (Buddy)
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={briefingActionId === b.id}
                      onClick={() => setRejectingId(rejectingId === b.id ? null : b.id)}
                      className="gap-2 border-sovereign/40 text-sovereign hover:bg-sovereign/10"
                      data-testid={`briefing-reject-${b.id}`}
                    >
                      <XCircle className="h-3.5 w-3.5" /> Reject
                    </Button>
                  </div>
                )}
                {rejectingId === b.id && (
                  <div className="mt-3 flex gap-2" data-testid={`briefing-reject-form-${b.id}`}>
                    <Input
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Reason for rejecting this briefing..."
                      data-testid={`briefing-reject-reason-input-${b.id}`}
                    />
                    <Button
                      size="sm"
                      disabled={!rejectReason.trim() || briefingActionId === b.id}
                      onClick={() => rejectBriefing(b.id)}
                      className="bg-sovereign text-white hover:bg-sovereign/90"
                      data-testid={`briefing-reject-confirm-${b.id}`}
                    >
                      Confirm
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
